import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsBoost from 'highcharts/modules/boost';
import React, { useEffect, useState } from 'react';
import {
  EReportDataInterval,
  ILogReport,
  ILogReportFilter,
} from '../types/report-log.type';
import {
  defaultYAxis,
  EChartColor,
  fillNullValues,
  fillNullValuesWithAverage,
  generateDashStyles,
  generateSeriesDataZone,
  hChartConfig,
  hexToRgba,
  toHHMMSS,
  toKnot,
} from './helperFunction';
import dayjs from 'dayjs';
import { forceChartToZero } from '../../../const/config';
import {
  ChartSchemaItem,
  ChartSeriesData,
  computeReportChartSeries,
} from '../pdf/reportPdfData';

HighchartsBoost(Highcharts);

const windowHeight = window.innerHeight;

export const zoneChartType = ['line', 'spline', 'areaspline', 'arealine'];

interface IProps {
  height?: number;
  logs: ILogReport;
  filter: ILogReportFilter;
  yAxis: string[];
  chartSchema: ChartSchemaItem[];
  isReportSchedule?: boolean;
  aggregatedLength?: number;
  stacking?: boolean;
  compact?: boolean;
  sumRunningTime?: boolean;
  runningTimeAt?: (timestamp: number) => number | null;
  // series calculated by the backend for the PDF page
  precomputedSeries?: ChartSeriesData;
}

const ReportChart: React.FC<IProps> = ({
  logs,
  filter,
  yAxis,
  chartSchema,
  isReportSchedule,
  height,
  aggregatedLength,
  stacking,
  compact = false,
  sumRunningTime = false,
  runningTimeAt,
  precomputedSeries,
}) => {
  const [dataChart, setDataChart] = useState<any>();

  const generateYAxis = (
    data: {
      color: string;
      text: string;
      opposite?: boolean;
      fixed?: boolean;
    }[]
  ) => {
    const yAxis: Highcharts.YAxisOptions | Array<Highcharts.YAxisOptions> = [];
    for (let idx = 0; idx < data.length; idx++) {
      const item = data[idx];
      yAxis.push({
        labels: {
          format: '{value}',
          style: {
            color: item.color,
          },
          formatter(
            this: Highcharts.AxisLabelsFormatterContextObject,
            ctx: Highcharts.AxisLabelsFormatterContextObject
          ) {
            if (item.text === 'Minute') {
              const seconds = Number(this.value);
              if (!Number.isFinite(seconds) || seconds < 0) {
                return '00:00:00';
              }
              return toHHMMSS(seconds);
            }
            if (item.fixed) {
              return Number(this.value).toFixed(2);
            }
            return this.value.toString();
          },
        },
        min: item.text === 'Minute' ? 0 : undefined,
        title: {
          text: item.text === 'Nautical Miles' ? 'Hour' : item.text,
          style: {
            color:
              item.text === 'Nautical Miles' ? 'rgb(111, 87, 233)' : item.color,
          },
        },
        lineColor:
          item.color === '#000000'
            ? 'rgba(0, 0, 0, 0.1)'
            : item.text === 'Nautical Miles'
            ? 'rgb(111, 87, 233)'
            : item.color,
        lineWidth: 1,
        tickColor:
          item.color === '#000000'
            ? 'rgba(0, 0, 0, 0.1)'
            : item.text === 'Nautical Miles'
            ? 'rgb(111, 87, 233)'
            : item.color,
        tickWidth: 1,
        tickLength: 5,
        opposite: item.opposite,
      });
    }
    return yAxis;
  };

  useEffect(() => {
    let ignore = false;
    if (!ignore && (precomputedSeries || (logs && Object.keys(logs).length))) {
      let currChartOption: any = {
        ...hChartConfig,
        // boost: {
        //   // useGPUTranslations: true,
        //   // seriesThreshold: 1,
        //   useGPUTranslations: true,
        //   usePreAllocated: true,
        //   allowForce: true,
        //   seriesThreshold: 1,
        // },
        yAxis: generateYAxis(
          defaultYAxis.filter((item) => yAxis.includes(item.key))
        ),
      };

      const { timestamps, dataSeries } =
        precomputedSeries ??
        computeReportChartSeries({
          logs,
          filter,
          chartSchema,
          aggregatedLength,
          sumRunningTime,
          runningTimeAt,
        });

      let isYAxisFuel = { isValid: false, index: 0 };
      let minValues: number[] = [];
      let maxValues: number[] = [];

      dataSeries.forEach((item) => {
        minValues.push(Math.min(...item.data));
        maxValues.push(Math.max(...item.data));
      });

      currChartOption.yAxis.forEach(
        (item: Highcharts.YAxisOptions, index: number) => {
          if (item.title?.text?.toLowerCase().includes('liter')) {
            isYAxisFuel = {
              isValid: true,
              index: index,
            };
          }
        }
      );

      const reportTimezone = filter.timezone ?? 7;

      currChartOption = {
        ...currChartOption,
        yAxis:
          forceChartToZero && minValues.length && isYAxisFuel.isValid
            ? currChartOption.yAxis.map(
                (item: Highcharts.YAxisOptions, index: number) => {
                  if (item.title?.text?.toLowerCase().includes('liter')) {
                    return {
                      ...item,
                      // min: Math.min(...minValues),
                      // max: Math.max(...maxValues),
                    };
                  }

                  return { ...item, linkedTo: isYAxisFuel.index };
                }
              )
            : currChartOption.yAxis,
        xAxis: [
          {
            categories: timestamps.map((item) =>
              dayjs(item * 1000)
                .utcOffset(reportTimezone * 60)
                .format(
                  filter.interval === EReportDataInterval.month
                    ? 'MMM YYYY'
                    : 'D/MM/YY HH:mm'
                )
            ),
            crosshair: true,
          },
        ],
        plotOptions: {
          series: {
            groupPadding: 0.1,
            pointPadding: 0,
            animation: false,
            // boostThreshold: 1000,
            // turboThreshold: 0,
            // marker: { enabled: false },
          },
          column: {
            stacking: stacking ? 'overlap' : undefined,
            dataLabels: {
              enabled: stacking ? true : false,
              formatter: function (this: any) {
                return this.y ? toHHMMSS(this.y) : '';
              },
            },
          },
        },
        tooltip: {
          shared: true,
          split: false,
          useHTML: true,
          borderWidth: 1,
          borderColor: 'rgba(0, 0, 0, 0.149)',
          backgroundColor: '#ffffff',
          borderRadius: 12,
          style: {
            fontSize: '11px',
            whiteSpace: 'nowrap',
          },
          formatter: function (
            this: Highcharts.TooltipFormatterContextObject
          ): string {
            let tooltip = `<strong style="display: block; font-size: 12px; margin-bottom: 4px;">${this.x}</strong>`;

            const MAX_SERIES = 6;
            const points = this.points ?? [];

            points.slice(0, MAX_SERIES).forEach((point) => {
              let suffix = '';
              let seriesName = point.series.name;
              let value: any = point.y;
              let color = point.series.color;

              if (point.series.name.toLowerCase().includes('time')) {
                value = toHHMMSS(point.y);
              } else if (point.series.name.toLowerCase().includes('fuel')) {
                suffix = 'Liter';
              } else if (point.series.name.toLowerCase().includes('rpm')) {
                suffix = 'RPM';
              } else if (point.series.name.toLowerCase().includes('speed')) {
                suffix = 'Knot';
                value = point.y && toKnot(point.y).toFixed(2);
              }

              tooltip += `
              <div style="display: grid; align-items: center; justify-content: flex-start; gap: 4px; grid-template-columns: repeat(4, auto);">
                <span style="display: inline-block; width: 7px; height: 7px; border-radius: 100px; background-color: ${color}; margin-right: 5px;"></span>
                <span>${seriesName}:</span>
                <strong>${value}</strong>
                <span>${suffix}</span>
              </div>
              `;
            });

            if (points.length > MAX_SERIES) {
              tooltip += `(+${points.length - MAX_SERIES} more series...)<br>`;
            }

            return tooltip;
          },
        },
        series: dataSeries.map((serie: any) => {
          const data = [...serie.data].map((value: unknown) => {
            if (value === undefined || value === null || value === '') {
              return null;
            }
            const numeric = typeof value === 'number' ? value : Number(value);
            if (!Number.isFinite(numeric)) {
              return null;
            }
            return numeric < 0 ? 0 : Number(numeric.toFixed(2));
          });

          let newData = data;
          let currentNullIndices: number[] = [];

          if (zoneChartType.includes(serie.type)) {
            // newData = fillNullValues(data);
            const { result, nullIndices } = fillNullValuesWithAverage(data);
            newData = result;
            currentNullIndices = nullIndices;
          }

          let dataZones: any[] = [];

          if (currentNullIndices.length) {
            dataZones = generateDashStyles(currentNullIndices, newData.length);
          }

          return {
            ...serie,
            data: newData,
            zoneAxis: 'x',
            connectNulls: true,
            inactiveOtherPoints: false,
            zones: dataZones,
          };
        }),
      };

      setDataChart(currChartOption);
    }

    return () => {
      ignore = true;
    };
  }, [logs, chartSchema, filter, aggregatedLength, sumRunningTime, stacking, yAxis, runningTimeAt, precomputedSeries]);

  const chartHeight = height || windowHeight - (isReportSchedule ? 160 : 260);
  const compactXAxis = Array.isArray(dataChart?.xAxis)
    ? dataChart.xAxis.map((axis: Highcharts.XAxisOptions) => ({
        ...axis,
        tickLength: 0,
        labels: {
          ...axis.labels,
          rotation: -48,
          step: Math.max(
            1,
            Math.ceil(((axis as any).categories?.length ?? 0) / 10)
          ),
          style: {
            ...axis.labels?.style,
            color: '#595959',
            fontSize: '6px',
            fontWeight: '400',
          },
        },
      }))
    : dataChart?.xAxis;
  const compactYAxis = Array.isArray(dataChart?.yAxis)
    ? dataChart.yAxis.map((axis: Highcharts.YAxisOptions) => {
        const axisTitle = String(axis.title?.text ?? '');
        const shouldHideAxis = ['Minute', 'Running Time'].includes(axisTitle);

        return {
          ...axis,
          labels: {
            ...axis.labels,
            enabled: shouldHideAxis ? false : axis.labels?.enabled,
            style: {
              ...axis.labels?.style,
              fontSize: '8px',
              fontWeight: '400',
            },
          },
          title: {
            ...axis.title,
            text: shouldHideAxis
              ? undefined
              : axisTitle === 'Liters'
              ? 'Liter'
              : axis.title?.text,
            style: {
              ...axis.title?.style,
              fontSize: '8px',
              fontWeight: '400',
            },
          },
          gridLineColor: shouldHideAxis
            ? 'transparent'
            : 'rgba(89, 89, 89, 0.12)',
          lineWidth: shouldHideAxis ? 0 : axis.lineWidth,
          tickLength: 0,
          tickWidth: shouldHideAxis ? 0 : axis.tickWidth,
        };
      })
    : dataChart?.yAxis;

  return (
    <React.Fragment>
      <HighchartsReact
        containerProps={{
          style: {
            marginTop: compact ? 10 : 20,
            height: chartHeight,
            width: '100%',
          },
        }}
        highcharts={Highcharts}
        options={{
          ...dataChart,
          chart: compact
            ? {
                ...(dataChart?.chart ?? {}),
                backgroundColor: 'transparent',
                events: {},
                height: chartHeight,
                marginTop: 8,
                marginRight: 28,
                marginBottom: 86,
                marginLeft: 52,
                spacingTop: 0,
                spacingRight: 0,
                spacingBottom: 0,
                spacingLeft: 0,
              }
            : {
                zoomType: 'xy',
                events: {},
              },
          ...(compact
            ? {
                xAxis: compactXAxis,
                yAxis: compactYAxis,
                legend: {
                  ...(dataChart?.legend ?? {}),
                  enabled: true,
                  align: 'center',
                  verticalAlign: 'bottom',
                  layout: 'horizontal',
                  floating: false,
                  width: 220,
                  itemDistance: 8,
                  itemMarginTop: 0,
                  itemMarginBottom: 0,
                  margin: 4,
                  padding: 0,
                  symbolWidth: 10,
                  symbolHeight: 6,
                  symbolRadius: 2,
                  itemStyle: {
                    color: '#3f4c5f',
                    fontSize: '8px',
                    fontWeight: '400',
                  },
                  itemHoverStyle: {
                    color: '#3f4c5f',
                  },
                },
                plotOptions: {
                  ...(dataChart?.plotOptions ?? {}),
                  series: {
                    ...(dataChart?.plotOptions?.series ?? {}),
                    lineWidth: 1,
                    states: {
                      ...(dataChart?.plotOptions?.series?.states ?? {}),
                      inactive: {
                        opacity: 1,
                      },
                      hover: {
                        lineWidthPlus: 0,
                      },
                    },
                  },
                  column: {
                    ...(dataChart?.plotOptions?.column ?? {}),
                    borderWidth: 0,
                    groupPadding: 0.08,
                    maxPointWidth: 8,
                    pointPadding: 0.04,
                  },
                },
              }
            : {}),
        }}
        allowChartUpdate={true}
        immutable={true}
      />
    </React.Fragment>
  );
};

export default ReportChart;
